import type { ClientMessageData } from '@shared/api';
import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import { Virtuoso, type Components, type StateSnapshot, type VirtuosoHandle } from 'react-virtuoso';
import type { VirtuosoContext } from './lib/types';
import { Scroller } from './ui';
import { Flex, Spin } from 'antd';
import { ItemContent } from './ui/list-components/item-content';
import { useDebouncedFunc } from '@shared/hooks/use-debounced-func';
import { useListLayout } from './hooks/use-list-layout';
import { DateService } from '@shared/services/date';

export const MESSAGES_LIST_ID = 'messages-list-id';

interface VirtualizedMessagesListProps {
  stateSnapshot?: StateSnapshot;
  clientId: number;
  totalCount: number;
  messages: ClientMessageData[];
  isFetchingMore: boolean;
  hasMore: boolean;
  fetchNextData: () => void;
  clientName?: string | null;
  clientAvatar?: string | null;
  agentNamesById?: Record<number, string>;
  switchNoticePrefix: string;
  switchNoticeTooltip: string;
  aiAgentLabel: string;
  onChangeMessage: (message: ClientMessageData) => void;
  onStateSnapshotChange: (snapshot: { state: StateSnapshot; clientId: number }) => void;
}

export type UiMessage = ClientMessageData & {
  showDateSeparator: boolean;
};

const Header: Components<ClientMessageData, VirtuosoContext>['Footer'] = ({ context }) => {
  if (!context?.isFetchingMore) return null;
  return (
    <Flex style={{ width: '100%' }} justify="center" align="center">
      <Spin />
    </Flex>
  );
};

export const VirtualizedMessagesList: FC<VirtualizedMessagesListProps> = ({
  stateSnapshot,
  clientId,
  totalCount,
  messages,
  isFetchingMore,
  hasMore,
  fetchNextData,
  clientName,
  clientAvatar,
  agentNamesById,
  switchNoticePrefix,
  switchNoticeTooltip,
  aiAgentLabel,
  onStateSnapshotChange,
  onChangeMessage,
}) => {
  const virtuoso = useRef<VirtuosoHandle>(null);
  const startReachedCooldownRef = useRef(0);
  const hasTriggeredAtTopRef = useRef(false);
  const isAtTopRef = useRef(false);
  const pendingTopFetchRef = useRef(false);
  const [allowRestore, setAllowRestore] = useState(Boolean(stateSnapshot));
  const lastDialogIndex = messages.length - 1;
  const baseItemIndex = Math.max(totalCount - messages.length, 0);

  const { firstItemIndex, followOutput, initialTopMostItemIndex } = useListLayout({
    id: clientId,
    messages,
    stateSnapshot,
    firstItemIndex: baseItemIndex,
  });

  const uiMessages: UiMessage[] = useMemo(() => {
    return (messages ?? []).map((msg, index, arr) => {
      const prev = index > 0 ? arr[index - 1] : undefined;
      const showDateSeparator =
        !prev || !DateService.isSame(msg.created_at, prev.created_at, 'day');

      return { ...msg, showDateSeparator };
    });
  }, [messages]);

  const handleScroll = useDebouncedFunc(() => {
    virtuoso.current?.getState((state) => {
      onStateSnapshotChange({ state, clientId });
    });
  }, 200);

  useEffect(() => {
    setAllowRestore(Boolean(stateSnapshot));
  }, [clientId]);

  useEffect(() => {
    startReachedCooldownRef.current = Date.now() + 300;
    hasTriggeredAtTopRef.current = false;
    isAtTopRef.current = false;
    pendingTopFetchRef.current = false;
  }, [clientId]);

  useEffect(() => {
    if (isFetchingMore) return;
    pendingTopFetchRef.current = false;
    if (isAtTopRef.current && hasMore) {
      hasTriggeredAtTopRef.current = false;
      requestMoreIfNeeded();
    }
  }, [isFetchingMore]);

  const requestMoreIfNeeded = () => {
    if (!hasMore || isFetchingMore) return;
    if (Date.now() < startReachedCooldownRef.current) return;
    if (pendingTopFetchRef.current) return;

    pendingTopFetchRef.current = true;
    hasTriggeredAtTopRef.current = true;
    fetchNextData();
  };

  useEffect(() => {
    if (!isAtTopRef.current) return;
    requestMoreIfNeeded();
  }, [messages.length, hasMore]);

  return (
    <Virtuoso<UiMessage, VirtuosoContext>
      ref={virtuoso}
      id={MESSAGES_LIST_ID}
      key={clientId}
      style={{
        flex: '1 1 auto',
        minHeight: 0,
        width: '100%',
      }}
      data={uiMessages}
      totalCount={totalCount}
      startReached={undefined}
      // endReached={fetchNextData}
      components={{ Header, Scroller }}
      computeItemKey={(_, item) => item.id}
      itemContent={ItemContent}
      onScroll={handleScroll}
      followOutput={followOutput}
      atBottomStateChange={(isAtBottom) => {
        setAllowRestore(!isAtBottom);
      }}
      atTopStateChange={(isAtTop) => {
        isAtTopRef.current = isAtTop;
        if (!isAtTop) {
          hasTriggeredAtTopRef.current = false;
          return;
        }
        if (Date.now() < startReachedCooldownRef.current) return;
        if (hasTriggeredAtTopRef.current) return;

        requestMoreIfNeeded();
      }}
      atBottomThreshold={200}
      alignToBottom
      firstItemIndex={firstItemIndex}
      context={{
        onChangeMessage,
        lastDialogIndex,
        messages: messages,
        clientName,
        clientAvatar,
        agentNamesById,
        switchNoticePrefix,
        switchNoticeTooltip,
        aiAgentLabel,
        isFetchingMore,
      }}
      // initialTopMostItemIndex={{ index: 'LAST' }}
      overscan={500}
      defaultItemHeight={80}
      initialTopMostItemIndex={initialTopMostItemIndex}
      increaseViewportBy={{ top: 1000, bottom: 1000 }}
      skipAnimationFrameInResizeObserver
      restoreStateFrom={allowRestore ? stateSnapshot : undefined}
    />
  );
};
